/** Swipe horizontal vira a página (US-02, critério "swipe horizontal em touch").
 *
 * Pointer Events e não Touch Events: um só caminho para dedo, caneta e mouse, e
 * é o `pointerType` que permite recusar o mouse — arrastar com o botão
 * pressionado é SELECIONAR texto, e tratar isso como virada de página tornaria
 * o destaque impossível de criar no desktop.
 *
 * Toda a decisão mora em `classificarSwipe`, puro. Aqui ficam só a coleta dos
 * dois pontos e o `pointercancel`, que é o que dispara quando o navegador
 * assume o gesto (rolagem nativa, pull-to-refresh) e sem o qual a virada
 * aconteceria em cima da rolagem que o próprio navegador já fez.
 */
import { useEffect, useRef } from 'react'

import { classificarSwipe, type Ponto } from '@/features/reader/utils/swipe'

interface Opcoes {
  alvo: HTMLElement | null
  onAnterior: () => void
  onProxima: () => void
  habilitado?: boolean
  /** Página mais larga que a área visível: o arrasto é rolagem, não virada. */
  rolagemHorizontal?: boolean
}

export function useSwipeNavigation({
  alvo,
  onAnterior,
  onProxima,
  habilitado = true,
  rolagemHorizontal = false,
}: Opcoes): void {
  // Ref para os callbacks não reassinarem os listeners a cada render — trocar
  // o listener no meio de um gesto perderia o `pointerup` e o swipe sumiria.
  const atual = useRef({ onAnterior, onProxima, rolagemHorizontal })
  useEffect(() => {
    atual.current = { onAnterior, onProxima, rolagemHorizontal }
  })

  useEffect(() => {
    if (!alvo || !habilitado) return

    let inicio: Ponto | null = null
    let ehMouse = false

    const aoDescer = (evento: PointerEvent) => {
      inicio = { x: evento.clientX, y: evento.clientY, t: evento.timeStamp }
      ehMouse = evento.pointerType === 'mouse'
    }

    const aoSubir = (evento: PointerEvent) => {
      if (!inicio) return
      const fim = { x: evento.clientX, y: evento.clientY, t: evento.timeStamp }
      const partida = inicio
      inicio = null

      const gesto = classificarSwipe(partida, fim, {
        ehMouse,
        rolagemHorizontal: atual.current.rolagemHorizontal,
      })
      if (gesto === 'proxima') atual.current.onProxima()
      else if (gesto === 'anterior') atual.current.onAnterior()
    }

    // O navegador assumiu o gesto: abandonar é o certo — virar a página aqui
    // somaria a virada à rolagem que ele já fez.
    const aoCancelar = () => {
      inicio = null
    }

    alvo.addEventListener('pointerdown', aoDescer)
    alvo.addEventListener('pointerup', aoSubir)
    alvo.addEventListener('pointercancel', aoCancelar)
    return () => {
      alvo.removeEventListener('pointerdown', aoDescer)
      alvo.removeEventListener('pointerup', aoSubir)
      alvo.removeEventListener('pointercancel', aoCancelar)
    }
  }, [alvo, habilitado])
}
